import React, { useState, useEffect } from 'react';
import {
	SdfSpotIllustration,
	SdfButton,
	SdfBox,
	SdfShimmer,
	SdfSwitch,
	SdfFocusPane,
	SdfTooltip,
	SdfBusyIndicator
} from '@synerg/react-components';
import CreateClientProject from './modal/createClientProject';
import Toast from '../common/toast';
import { ACCELERATOR_STATUS } from '../constants';
import SubscriberInfoModal from './steps/subscriberInfoModal';
import CopyToClipboard from 'react-copy-to-clipboard';
import { uniqueId } from 'lodash';
import './clientProjectDetails.scss';

export default function ProjectCredentials({
	projectDetails,
	credValues,
	setCredValues,
	regenerateSIClientAppCreds,
	projectExists,
	loadingClientId,
	loadingClientSecret,
	fetchSIProjectDetails,
	carDetails,
	carDetailsLoading
}) {
	const [confirmB2bRegenerate, setConfirmB2bRegenerate] = useState(false);
	const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
	const [toast, setToast] = useState(false);
	const [showSubscriberInfo, setShowSubscriberInfo] = useState(false);
	const [hideSubscriberInfoButton, setHideSubscriberInfoButton] = useState(true);
	const isPartnerAppSubscribedOrCreated = projectDetails.status === ACCELERATOR_STATUS.PARTNER_APP_SUBSCRIBED || projectDetails.status === ACCELERATOR_STATUS.CLIENT_APP_CREATED;

	useEffect(() => {
		if (projectDetails?.status === ACCELERATOR_STATUS.COMPLETE) {
			if (projectDetails?.clientProjectDetails?.consentInfo?.status !== 'allow' && projectDetails.commitChangesCount < 1) {
				setHideSubscriberInfoButton(false);
			}
			else {
				setHideSubscriberInfoButton(true);
			}
		}

		else if (projectDetails?.status === ACCELERATOR_STATUS.CLIENT_APP_SUBSCRIBED) {
			setHideSubscriberInfoButton(false);
		}

		else {
			setHideSubscriberInfoButton(true);
		}
	}, [projectDetails]);


	useEffect(() => {
		if (toast) {
			fetchSIProjectDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [toast]);
	const copyText = (value = '') => {
		navigator.clipboard.writeText(value);
	};

	const Confirmation = ({ confirmationMessage }) => {
		return (<SdfFocusPane
			paneType="floating"
			visible={true}
			size="small"
			hideAcceptButton={true}
			hideDismissButton={true}
		>
			<span slot="title">Are you sure?</span>
			<div className="text-body-01 mx-6 text-left">
				{confirmationMessage}
			</div>
			<div slot="custom-buttons" className="mt-3">
				<SdfButton onClick={() => setConfirmB2bRegenerate(false)}>
					Cancel
				</SdfButton>
				<SdfButton
					emphasis="primary"
					onClick={() => {
						regenerateSIClientAppCreds(credValues.consumerAppID,
							credValues.projectID, projectDetails.clientOOID);
						setConfirmB2bRegenerate(false);
					}}
					className="ml-3"
				>
					<div style={{ height: '1.625rem' }}>
						Yes
					</div>
				</SdfButton>
			</div>
		</SdfFocusPane>);
	};

	const toggleShowSecret = (toggled) => {
		setCredValues({ type: 'UPDATE', payload: { showB2bSecret: toggled } });
	};

	const credentialState = (loader, credential, status) => {
		if (status === ACCELERATOR_STATUS.PARTNER_APP_SUBSCRIBED || status === ACCELERATOR_STATUS.CLIENT_APP_CREATED) {
			return <div className='min-w-24'>Not Applicable</div>
		} else if (loader) {
			return <SdfShimmer className="w-sm" />;
		} else {
			return <div className='min-w-24 break-all'>{credential}</div>;
		}
	}

	const GetCredentials = ({
		clientId = '',
		clientSecret = '',
		showSecret,
		loadingClientId,
		loadingSecret,
	}) => {
		return (<div>
			<div className="flex items-start justify-between col-gap-6">
				<div className="flex-1 font-bold min-w-24">ClientID</div>
				{credentialState(loadingClientId, clientId, projectDetails.status)}
				<div>
					<SdfTooltip positionContext="ELEMENT" trigger="click" state="info">
						<SdfButton
							slot="tooltip-target"
							emphasis="primary"
							variant="contained"
							onClick={() => copyText(clientId)}
							{...((loadingClientId || !clientId || isPartnerAppSubscribedOrCreated)
								? { disabled: true }
								: { disabled: undefined })}
						>

							Copy
						</SdfButton>
						Copied
					</SdfTooltip>
				</div>
			</div>

			<div className={`mt-6 grid ${showSecret ? 'visible-secret-container' : 'hidden-secret-container'} col-gap-6 row-gap-4 items-center w-full`}>
				<div className="font-bold min-w-24">Client secret</div>
				<div className="reveal-button mt-1">
					<SdfSwitch
						label="Reveal"
						checked={showSecret}
						{...(((!clientSecret) || isPartnerAppSubscribedOrCreated) && { disabled: true })}
						onSdfChange={(event) => {
							toggleShowSecret(event.detail);
						}}
					/>
				</div>
				<div>
					{showSecret
						? (loadingSecret
							? <SdfShimmer className="block w-xs" />
							: <div className="secret-text">{clientSecret}</div>)
						: <div className='mt-1 font-bold'>**************************</div>}
				</div>
				<SdfButton
					slot="buttons"
					emphasis="tertiary"
					variant="text"
					icon="action-refresh"
					className="justify-self-end"
					{...((!clientSecret || loadingSecret || isPartnerAppSubscribedOrCreated) && { disabled: true })}
					onClick={() => { setConfirmB2bRegenerate(true); }}
				>
					Regenerate
				</SdfButton>

				<SdfTooltip positionContext="ELEMENT" trigger="click" state="info" className='justify-self-end copy-secret-button'>
					<SdfButton
						slot="tooltip-target"
						emphasis="primary"
						variant="contained"
						onClick={() => copyText(clientSecret)}
						{...((loadingSecret || !clientSecret || isPartnerAppSubscribedOrCreated)
							? { disabled: true }
							: { disabled: undefined })}
					>
						Copy
					</SdfButton>
					Copied
				</SdfTooltip>
			</div>
		</div>);
	};


	return (<div className="block w-full">

		<div className="text-heading-05 font-bold w-full">Credentials</div>

		{!projectExists ? <div
			className="no-credentials mt-20 text-center h-xs flex flex-col row-gap-8">
			<SdfSpotIllustration illustrationName="empty-clipboard" size="lg" />
			<div className="my-5">
				<div
					className="text-heading-04 text-secondary font-bold text-tertiary mb-2">No
					Credentials found. Please create a project first in order to
					generate credentials.
				</div>
				<div className="text-tertiary">Get started by creating a project for
					this client.
				</div>
			</div>
			<div>
				<SdfButton emphasis="primary"
					onClick={() => setShowCreateProjectModal(true)}
					{...(projectDetails.hasOwnProperty("isClientInAPIC") && !projectDetails.isClientInAPIC && { disabled: true })}
				>
					Create new project
				</SdfButton>
			</div>
		</div> :
			<div className="w-full mt-5">
				<SdfBox className="mt-6" variant="filled" spacing="loose">
					<GetCredentials
						clientType="B2b"
						clientId={credValues.b2bClientId}
						clientSecret={credValues.b2bClientSecret}
						showSecret={credValues.showB2bSecret}
						loadingClientId={loadingClientId}
						loadingSecret={loadingClientSecret}
					/>
				</SdfBox>
				<div>

					<div className="text-heading-05 font-bold w-full mt-16">
						Consent authorization
					</div>
					<SdfBox className="mt-6" variant="bordered" spacing="loose">
						<div className="flex font-bold">Client Authorization Link</div>
						<div className="mt-4 flex justify-between items-center col-gap-8 row-gap-6 consent-box">
							{<div className={`mb-1 break-all ${hideSubscriberInfoButton ? 'max-w-10/12' : 'max-w-2/3 url-width'}`}>{projectDetails?.clientProjectDetails?.consentURL || 'NA'}</div>}
							<div className='flex justify-between items-center col-gap-4'>
								{!hideSubscriberInfoButton && <SdfButton
									className='whitespace-no-wrap'
									slot="tooltip-target"
									emphasis="primary"
									variant="contained"
									onClick={() => setShowSubscriberInfo(true)}
									{...(carDetailsLoading || !carDetails?.subscriberInfo) && { disabled: true }}
								>
									<div style={{ height: '1.625rem' }}>
										<div>View subscriber info</div>
										{carDetailsLoading && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
									</div>
								</SdfButton>}

								{projectDetails?.clientProjectDetails?.consentURL ? <CopyToClipboard text={projectDetails.clientProjectDetails.consentURL}>
									<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
										<SdfButton emphasis="secondary" slot="tooltip-target"> Copy </SdfButton>
										Copied
									</SdfTooltip>
								</CopyToClipboard>
									:
									<SdfButton disabled={true} emphasis="secondary" slot="tooltip-target"> Copy </SdfButton>}
							</div>
						</div>
					</SdfBox>
				</div>
			</div>}

		{showSubscriberInfo && <SubscriberInfoModal carDetails={carDetails} setShowSubscriberInfo={setShowSubscriberInfo} />}

		{confirmB2bRegenerate && <Confirmation
			confirmationMessage={'Regenerating the client secret will cause the existing integration to break. Click Yes if you want to proceed.'} />}

		{showCreateProjectModal && <CreateClientProject
			setShowCreateProjectModal={setShowCreateProjectModal}
			projectDetails={projectDetails} setToast={setToast} />}

		{toast && (<Toast
			key={toast.key}
			status={toast.status}
			message={toast.message}
			showToast={toast.showToast}
		/>)}
	</div>);
}
